import React, { JSX, useEffect, useRef, useState } from 'react'
import { graphqlRequest } from 'lib/helpers'
import { AppSyncClient, WEBSOCKET_SUB_QUERY, buildAppSyncWsClient } from 'lib/appSyncSubscription'
import { ensureFontLoaded } from 'lib/fontLoader'
import { OverlayAutoFit } from 'components/OverlayAutoFit'
import {
  LogoNaturalSize,
  TICKER_BAR_MARQUEE_WIDTH,
  TickerBarEnvelope,
  TickerBarMarquee,
  isTickerBarDeletion,
  parseTickerBarEnvelope
} from 'components/TickerBarMarquee'
import type { TickerBarData, TickerBarStyle } from 'types/api'
import getTickerBarDataByIdQuery from 'graphql/getTickerBarDataById.graphql'
import logger from 'logger'

const log = logger.extend('ticker-bar')

const BOOTSTRAP_RETRY_MS = 15_000
const LOGO_WAIT_CAP_MS = 5000

export interface TickerBarViewState {
  envelope: TickerBarEnvelope | null
  version: number | null
  gone: boolean
  restartKey: number
}

export const INITIAL_TICKER_BAR_VIEW_STATE: TickerBarViewState = {
  envelope: null,
  version: null,
  gone: false,
  restartKey: 0
}

export const foldTickerBarEnvelope = (
  current: TickerBarViewState,
  incoming: unknown,
  tickerBarId: string
): TickerBarViewState => {
  const frame = parseTickerBarEnvelope(incoming)
  if (frame == null || frame.tickerBarId !== tickerBarId) return current
  if (current.version != null && frame.version <= current.version) return current
  if (isTickerBarDeletion(frame)) {
    return { envelope: null, version: frame.version, gone: true, restartKey: current.restartKey }
  }
  return {
    envelope: frame,
    version: frame.version,
    gone: false,
    restartKey: frame.restart ? current.restartKey + 1 : current.restartKey
  }
}

export const tickerBarGone = (current: TickerBarViewState): TickerBarViewState =>
  current.gone && current.envelope == null ? current : { ...current, envelope: null, gone: true }

export const connectTickerBarSocket = ({
  client,
  channel,
  onFrame,
  onConnected,
  onError
}: {
  client: Pick<AppSyncClient, 'on' | 'subscribe'>
  channel: string
  onFrame: (raw: unknown) => void
  onConnected: () => void
  onError?: (err: unknown) => void
}): (() => void) => {
  const offConnected = client.on('connected', onConnected)
  const unsubscribe = client.subscribe(
    { query: WEBSOCKET_SUB_QUERY, variables: { channel } },
    {
      next: (data: unknown) => {
        const raw = (data as { WebsocketSub?: { data?: unknown } } | null)?.WebsocketSub?.data
        if (raw == null) return
        onFrame(raw)
      },
      error: (err: unknown) => onError?.(err),
      complete: () => { }
    }
  )
  return () => {
    offConnected()
    unsubscribe()
  }
}

export const tickerBarFontKeysInUse = (style: TickerBarStyle): string[] => {
  const bars = style.showTopBar ? [style.top, style.bottom] : [style.bottom]
  return [...new Set(bars.flatMap((b) => [b.labelFontKey, b.textFontKey]))]
}

export const tickerBarLogoUrlsInUse = (style: TickerBarStyle): string[] => {
  const bars = style.showTopBar ? [style.top, style.bottom] : [style.bottom]
  const urls: string[] = []
  for (const b of bars) {
    if (b.logoOn && b.logoUrl != null && b.logoUrl !== '' && !urls.includes(b.logoUrl)) urls.push(b.logoUrl)
  }
  return urls
}

const preloadLogo = async (url: string): Promise<LogoNaturalSize | 'failed' | null> =>
  await new Promise((resolve) => {
    if (typeof globalThis.Image === 'undefined') {
      resolve(null)
      return
    }
    const img = new globalThis.Image()
    let settled = false
    const done = (value: LogoNaturalSize | 'failed' | null): void => {
      if (settled) return
      settled = true
      globalThis.clearTimeout(timer)
      img.onload = null
      img.onerror = null
      resolve(value)
    }
    const timer = globalThis.setTimeout(() => done(null), LOGO_WAIT_CAP_MS)
    img.onload = () => done(img.naturalWidth > 0 && img.naturalHeight > 0
      ? { width: img.naturalWidth, height: img.naturalHeight }
      : 'failed')
    img.onerror = () => done('failed')
    img.src = url
  })

const useFirstPaint = (envelope: TickerBarEnvelope | null): { ready: boolean, logoSizes: Record<string, LogoNaturalSize | 'failed'> } => {
  const [ready, setReady] = useState(false)
  const [logoSizes, setLogoSizes] = useState<Record<string, LogoNaturalSize | 'failed'>>({})
  const fonts = envelope == null ? [] : tickerBarFontKeysInUse(envelope.style)
  const logos = envelope == null ? [] : tickerBarLogoUrlsInUse(envelope.style)
  const gateKey = envelope == null ? null : JSON.stringify([fonts, logos])
  useEffect(() => {
    if (ready || gateKey == null) return
    let cancelled = false
    void Promise.all([
      Promise.all(fonts.map(async (key) => await ensureFontLoaded(key))),
      Promise.all(logos.map(async (url) => [url, await preloadLogo(url)] as const))
    ]).then(([, sizes]) => {
      if (cancelled) return
      const known: Record<string, LogoNaturalSize | 'failed'> = {}
      for (const [url, size] of sizes) {
        if (size != null) known[url] = size
      }
      setLogoSizes(known)
      setReady(true)
    })
    return () => { cancelled = true }
  }, [ready, gateKey])
  return { ready, logoSizes }
}

const errName = (err: unknown): string => (err instanceof Error ? err.name : 'unknown')

const useTickerBarFeed = ({ tickerBarId, token, slug }: {
  tickerBarId: string
  token: string
  slug: string | null
}): TickerBarViewState => {
  const [state, setState] = useState<TickerBarViewState>(INITIAL_TICKER_BAR_VIEW_STATE)
  const stateRef = useRef(state)
  const [channel, setChannel] = useState<string | null>(null)
  const loadRef = useRef<() => void>(() => {})
  const commit = (next: TickerBarViewState): void => {
    if (next === stateRef.current) return
    stateRef.current = next
    setState(next)
  }
  const applyRef = useRef((raw: unknown): void => {})
  applyRef.current = (raw: unknown): void => commit(foldTickerBarEnvelope(stateRef.current, raw, tickerBarId))
  const goneRef = useRef((): void => {})
  goneRef.current = (): void => commit(tickerBarGone(stateRef.current))

  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof globalThis.setTimeout> | undefined
    const load = (trigger: 'mount' | 'connected' | 'retry'): void => {
      graphqlRequest<{ tickerBarData: TickerBarData | null }>(
        getTickerBarDataByIdQuery,
        slug != null ? { tickerBarId, slug } : { tickerBarId },
        token
      )
        .then((res) => {
          if (cancelled) return
          if (res.tickerBarData == null) {
            log({ bootstrap: 'gone', trigger, tickerBarId, viaSlug: slug != null })
            goneRef.current()
            return
          }
          applyRef.current(res.tickerBarData.envelope)
          setChannel(res.tickerBarData.channel ?? null)
        })
        .catch((err: unknown) => {
          log({ bootstrap: 'failed', trigger, tickerBarId, err: errName(err) })
          if (!cancelled) retryTimer = globalThis.setTimeout(() => load('retry'), BOOTSTRAP_RETRY_MS)
        })
    }
    loadRef.current = () => load('connected')
    load('mount')
    return () => {
      cancelled = true
      globalThis.clearTimeout(retryTimer)
    }
  }, [tickerBarId, token, slug])

  useEffect(() => {
    if (channel == null || channel === '' || token === '') return
    const client = buildAppSyncWsClient(token, { persistent: true })
    const disconnect = connectTickerBarSocket({
      client,
      channel,
      onFrame: (raw) => applyRef.current(raw),
      onConnected: () => loadRef.current(),
      onError: (err) => log({ subscription: 'error', tickerBarId, err: errName(err) })
    })
    return () => {
      disconnect()
      client.dispose()
    }
  }, [channel, token, tickerBarId])

  return state
}

const TickerBarFeedView = ({
  tickerBarId,
  token,
  slug,
  wrapWidth,
  onEnvelope
}: {
  tickerBarId: string
  token: string
  slug: string | null
  wrapWidth?: number
  onEnvelope?: (envelope: TickerBarEnvelope | null) => void
}): JSX.Element | null => {
  const state = useTickerBarFeed({ tickerBarId, token, slug })
  const { ready, logoSizes } = useFirstPaint(state.envelope)
  const onEnvelopeRef = useRef(onEnvelope)
  onEnvelopeRef.current = onEnvelope
  useEffect(() => {
    onEnvelopeRef.current?.(state.envelope)
  }, [state.envelope])
  if (state.envelope == null || !ready) return null
  return (
    <TickerBarMarquee
      envelope={state.envelope}
      wrapWidth={wrapWidth}
      restartKey={state.restartKey}
      logoSizes={logoSizes}
    />
  )
}

export const TickerBarOverlayBody = ({
  tickerBarId,
  token
}: {
  tickerBarId: string
  token: string
}): JSX.Element => {
  const [pageBackground, setPageBackground] = useState('transparent')
  useEffect(() => {
    if (pageBackground === 'transparent') return
    const body = globalThis.document.body
    body.style.background = pageBackground
    return () => {
      body.style.background = 'transparent'
    }
  }, [pageBackground])
  return (
    <OverlayAutoFit mode={{ kind: 'design', width: TICKER_BAR_MARQUEE_WIDTH }}>
      <TickerBarFeedView
        key={tickerBarId}
        tickerBarId={tickerBarId}
        token={token}
        slug={null}
        onEnvelope={(envelope) => setPageBackground(envelope?.style.pageBackground ?? 'transparent')}
      />
    </OverlayAutoFit>
  )
}

export const TickerBarSceneView = ({
  tickerBarId,
  slug,
  token,
  wrapWidth
}: {
  tickerBarId: string
  slug: string
  token: string
  wrapWidth?: number
}): JSX.Element => (
  <TickerBarFeedView
    key={`${slug}:${tickerBarId}`}
    tickerBarId={tickerBarId}
    token={token}
    slug={slug}
    wrapWidth={wrapWidth}
  />
)
